import { useEffect, useRef, useState } from 'react';

import { ReactComponent as DownArrow } from '../../assets/icons/down-arrow.svg';
import { TooltipIcon } from '../tooltip/TooltipIcon';

type InputType = 'text' | 'number' | 'email' | 'password' | 'tel';
interface CustomInputProperties {
  inputLabel?: string;
  type: InputType;
  placeholder: string;
  value: string | number;
  value1?: string | number;
  name: string;
  onChangeValue?: (name: string, value: string | number) => void;
  valueRequired?: boolean;
  readOnly?: boolean;
  error?: boolean;
  errorMessage?: string;
  disableDropdown?: boolean;
  tooltipText?: string;
  minLength?: number;
  maxLength?: number;
}

/**
 * Custom Input box function component
 * @param {any} param0 - The param0
 * @param {any} param0.inputLabel - The inputLabel prop
 * @param {any} param0.type - The type prop
 * @param {any} param0.name - The name prop
 * @param {any} param0.placeholder - The placeholder prop
 * @param {any} param0.value - The value prop
 * @param {any} param0.value1 - The value1 prop
 * @param {any} param0.onChangeValue - The onChangeValue prop
 * @param {any} param0.valueRequired - The valueRequired prop
 * @param {any} param0.readOnly - The readOnly prop
 * @param {any} param0.error - The error prop
 * @param {any} param0.errorMessage - The errorMessage prop
 * @param {any} param0.disableDropdown - The disableDropdown prop
 * @param {any} param0.tooltipText - The tooltipText prop
 * @param {any} param0.minLength - The minLength prop
 * @param {any} param0.maxLength - The maxLength prop
 * @returns {any} The rendered component
 */
function CustomPhoneInput({
  inputLabel,
  type,
  name,
  placeholder,
  value,
  value1,
  onChangeValue = () => {},
  valueRequired,
  readOnly,
  error,
  errorMessage,
  disableDropdown,
  tooltipText,
  minLength,
  maxLength,
}: Readonly<CustomInputProperties>) {
  const listItems = ['1', '91', '966', '971'];
  const reference = useRef<HTMLDivElement>(null);
  const [list, setList] = useState<boolean>(false);

  /**
   * Input box change handle function. Enforces maxLength manually since
   * the HTML maxLength attribute is ignored by browsers on type="number" inputs.
   * @param {string | number} changeValue - The new input value
   */
  const handleValueChange = (changeValue: string | number): void => {
    const stringValue = String(changeValue);
    if (maxLength && stringValue.length > maxLength) {
      return;
    }
    onChangeValue(name, changeValue);
  };

  /**
   * Toggle the dropdown popup function
   */
  const openPop = (): void => {
    if (disableDropdown) {
      return;
    }
    setList((previous) => !previous);
  };

  /**
   * Handle closing the dropdown function when clicking outside
   * @param {MouseEvent} event - The mouse event
   */
  const handleClickOutside = (event: MouseEvent): void => {
    if (reference.current && !reference.current.contains(event.target as Node)) {
      setList(false);
    }
  };

  useEffect((): (() => void) => {
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  return (
    <div className="custom-input-wrap">
      {inputLabel && (
        <p className="input-label">
          {inputLabel}
          {valueRequired && <span>*</span>}
          {tooltipText && <TooltipIcon text={tooltipText} />}
        </p>
      )}
      <div className={`custom-input-container ${error ? 'error' : ''}`}>
        <div
          className={`front-text ${disableDropdown ? 'disabled' : ''}`}
          onClick={openPop}
          role="presentation"
          ref={reference}>
          +{value1 || '1'}
          {!disableDropdown && (
            <span>
              <DownArrow className="icon" />
            </span>
          )}
          {list && (
            <div className="country-code-list">
              {listItems.map((item) => (
                <div
                  key={item}
                  className="list-item"
                  onClick={(event) => {
                    event.stopPropagation();
                    onChangeValue('countryCode', item);
                    setList(false);
                  }}
                  role="presentation">
                  {item}
                </div>
              ))}
            </div>
          )}
        </div>
        <input
          type={type}
          placeholder={placeholder}
          value={value}
          onWheel={(event) => {
            event.preventDefault();
          }}
          onChange={(event) => handleValueChange(event.target.value)}
          required={valueRequired}
          readOnly={readOnly}
          minLength={minLength}
          maxLength={maxLength}
        />
      </div>
      {error && <p className="text-danger">{errorMessage}</p>}
    </div>
  );
}

CustomPhoneInput.defaultProps = {
  inputLabel: '',
  valueRequired: false,
  readOnly: false,
  error: false,
  errorMessage: '',
  /**
   * Input change function
   */
  onChangeValue: () => {},
  value1: '91',
  disableDropdown: false,
  tooltipText: '',
  minLength: undefined,
  maxLength: undefined,
};

export default CustomPhoneInput;
